import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, TrendingUp, TrendingDown, Minus,
  FileText, CheckCircle2, XCircle, Clock, BarChart3,
  ChevronRight, X, Lightbulb, Award
} from 'lucide-react';
import api from '../../utils/api';


function getAuthHeaders() {
  const token = '';
  return {};
}

function TrendIcon({ current, previous, invert = false }) {
  if (previous == null || current == null) return <Minus className="h-3 w-3 text-slate-300" />;
  const diff = current - previous;
  if (Math.abs(diff) < 0.5) return <Minus className="h-3 w-3 text-slate-300" />;
  const isUp = diff > 0;
  const isGood = invert ? !isUp : isUp;
  return isGood
    ? <TrendingUp className="h-3 w-3 text-emerald-500" />
    : <TrendingDown className="h-3 w-3 text-red-400" />;
}

function KpiTile({ label, value, icon: Icon, color, delta, deltaLabel, invertTrend, testId }) {
  return (
    <Card className="border border-slate-200/60" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Icon className="h-4 w-4" style={{ color }} />
          {delta != null && (
            <span className={`text-[10px] font-medium flex items-center gap-0.5 ${delta >= 0 && !invertTrend ? 'text-emerald-600' : delta < 0 && !invertTrend ? 'text-red-500' : delta <= 0 && invertTrend ? 'text-emerald-600' : 'text-red-500'}`}>
              {delta > 0 ? '+' : ''}{delta}{deltaLabel || ''}
            </span>
          )}
        </div>
        <p className="text-2xl font-bold text-[#122033]">{value}</p>
        <p className="text-[10px] text-slate-400 mt-1">{label}</p>
      </CardContent>
    </Card>
  );
}

function DetailDrawer({ scorecard, onClose }) {
  const { t } = useTranslation();
  if (!scorecard) return null;
  const m = scorecard.metrics_snapshot || {};

  const tips = [];
  if (m.delta_acceptance_rate != null && m.delta_acceptance_rate < 0)
    tips.push(t('provider.scorecard.tipAcceptance'));
  if (m.delta_median_decision_hours != null && m.delta_median_decision_hours > 0)
    tips.push(t('provider.scorecard.tipDecisionTime'));
  if ((m.pending_count || 0) > 0)
    tips.push(t('provider.scorecard.tipPending', { count: m.pending_count }));
  if (tips.length === 0)
    tips.push(t('provider.scorecard.tipGreat'));

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end" data-testid="scorecard-detail-drawer">
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col h-full animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-base font-semibold text-[#122033]">{t('provider.scorecard.weekOf', { date: scorecard.week_start_utc })}</h2>
            <p className="text-xs text-slate-400">{scorecard.week_start_utc} — {scorecard.week_end_utc}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="drawer-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* KPI Table */}
          <div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">{t('provider.scorecard.perfSummary')}</h3>
            <table className="w-full text-sm" data-testid="detail-kpi-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 text-xs text-slate-400 font-medium">{t('provider.scorecard.colMetric')}</th>
                  <th className="text-right py-2 text-xs text-slate-400 font-medium">{t('provider.scorecard.colYourClinic')}</th>
                  <th className="text-right py-2 text-xs text-slate-400 font-medium">{t('provider.scorecard.colVsPlatform')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-50">
                  <td className="py-2 text-slate-600">{t('provider.scorecard.metricOffersSent')}</td>
                  <td className="py-2 text-right font-semibold">{m.quotes_sent_count ?? '—'}</td>
                  <td className="py-2 text-right text-slate-400">—</td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="py-2 text-slate-600">{t('provider.scorecard.metricAccepted')}</td>
                  <td className="py-2 text-right font-semibold text-emerald-600">{m.accepted_count ?? '—'}</td>
                  <td className="py-2 text-right text-slate-400">—</td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="py-2 text-slate-600">{t('provider.scorecard.metricAcceptanceRate')}</td>
                  <td className="py-2 text-right font-semibold">{m.acceptance_rate != null ? `${m.acceptance_rate}%` : '—'}</td>
                  <td className="py-2 text-right">
                    {m.delta_acceptance_rate != null ? (
                      <span className={m.delta_acceptance_rate >= 0 ? 'text-emerald-600' : 'text-red-500'}>
                        {m.delta_acceptance_rate > 0 ? '+' : ''}{m.delta_acceptance_rate}pp
                      </span>
                    ) : '—'}
                  </td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="py-2 text-slate-600">{t('provider.scorecard.metricMedianDecision')}</td>
                  <td className="py-2 text-right font-semibold">{m.median_time_to_decision_hours != null ? `${m.median_time_to_decision_hours}h` : '—'}</td>
                  <td className="py-2 text-right">
                    {m.delta_median_decision_hours != null ? (
                      <span className={m.delta_median_decision_hours <= 0 ? 'text-emerald-600' : 'text-red-500'}>
                        {m.delta_median_decision_hours > 0 ? '+' : ''}{m.delta_median_decision_hours}h
                      </span>
                    ) : '—'}
                  </td>
                </tr>
                <tr className="border-b border-slate-50">
                  <td className="py-2 text-slate-600">{t('provider.scorecard.metricRejected')}</td>
                  <td className="py-2 text-right text-red-500">{m.rejected_count ?? '—'}</td>
                  <td className="py-2 text-right text-slate-400">—</td>
                </tr>
                <tr>
                  <td className="py-2 text-slate-600">{t('provider.scorecard.metricPending')}</td>
                  <td className="py-2 text-right text-amber-500">{m.pending_count ?? '—'}</td>
                  <td className="py-2 text-right text-slate-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Top Treatments */}
          {m.top_treatments?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">{t('provider.scorecard.topTreatments')}</h3>
              <div className="flex flex-wrap gap-1.5">
                {m.top_treatments.map((tr, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{tr}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Tips */}
          <div data-testid="detail-tips">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
              <Lightbulb className="h-3 w-3" /> {t('provider.scorecard.improvementTips')}
            </h3>
            <ul className="space-y-2">
              {tips.map((tip, i) => (
                <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                  <span className="text-[#3BA4C5] mt-0.5">-</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClinicScorecardDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scorecards, setScorecards] = useState([]);
  const [error, setError] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);

  const fetchScorecards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/clinic/analytics/scorecards?limit=12`);
      if (res.status === 401 || res.status === 403) {
        navigate('/clinic/login');
        return;
      }
      const data = res.data;
      setScorecards(data.scorecards || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchScorecards(); }, [fetchScorecards]);

  const latest = scorecards[0]?.metrics_snapshot || {};
  const prev = scorecards[1]?.metrics_snapshot || {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="scorecard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="clinic-scorecard-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#122033] to-[#1a3352] text-white py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/clinic/dashboard')}
              className="text-white/70 hover:text-white hover:bg-white/10" data-testid="scorecard-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> {t('provider.scorecard.backToDashboard')}
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Award className="h-6 w-6 text-amber-300" />
            <div>
              <h1 className="text-xl font-semibold">{t('provider.scorecard.pageTitle')}</h1>
              <p className="text-sm text-blue-200">{t('provider.scorecard.weekHistory', { count: scorecards.length })}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
          </Card>
        )}

        {scorecards.length === 0 ? (
          <Card className="text-center py-12" data-testid="scorecard-empty">
            <BarChart3 className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-600 mb-2">{t('provider.scorecard.noScorecards')}</h2>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              {t('provider.scorecard.noScorecardsHelp')}
            </p>
          </Card>
        ) : (
          <>
            {/* Latest Week KPI Tiles */}
            <div>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                {t('provider.scorecard.latestWeek', { date: scorecards[0]?.week_start_utc })}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="latest-kpis">
                <KpiTile
                  label={t('provider.scorecard.kpiOffersSent')}
                  value={latest.quotes_sent_count ?? 0}
                  icon={FileText}
                  color="#3BA4C5"
                  delta={prev.quotes_sent_count != null ? (latest.quotes_sent_count || 0) - prev.quotes_sent_count : null}
                  testId="kpi-offers-sent"
                />
                <KpiTile
                  label={t('provider.scorecard.kpiAcceptanceRate')}
                  value={`${latest.acceptance_rate ?? 0}%`}
                  icon={CheckCircle2}
                  color="#10B981"
                  delta={prev.acceptance_rate != null ? Math.round(((latest.acceptance_rate || 0) - prev.acceptance_rate) * 10) / 10 : null}
                  deltaLabel="pp"
                  testId="kpi-acceptance-rate"
                />
                <KpiTile
                  label={t('provider.scorecard.kpiPending')}
                  value={latest.pending_count ?? 0}
                  icon={Clock}
                  color="#F59E0B"
                  delta={prev.pending_count != null ? (latest.pending_count || 0) - prev.pending_count : null}
                  invertTrend
                  testId="kpi-pending"
                />
                <KpiTile
                  label={t('provider.scorecard.kpiMedianDecision')}
                  value={latest.median_time_to_decision_hours != null ? `${latest.median_time_to_decision_hours}h` : '—'}
                  icon={BarChart3}
                  color="#6366F1"
                  delta={prev.median_time_to_decision_hours != null && latest.median_time_to_decision_hours != null
                    ? Math.round((latest.median_time_to_decision_hours - prev.median_time_to_decision_hours) * 10) / 10
                    : null}
                  deltaLabel="h"
                  invertTrend
                  testId="kpi-median-decision"
                />
              </div>
            </div>

            {/* History Table */}
            <Card className="border border-slate-200/60 shadow-sm" data-testid="scorecard-history">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#122033]">{t('provider.scorecard.history')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="history-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 text-[10px] uppercase text-slate-400 font-medium">{t('provider.scorecard.colWeek')}</th>
                        <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium">{t('provider.scorecard.colSent')}</th>
                        <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium">{t('provider.scorecard.colAcceptPct')}</th>
                        <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium hidden sm:table-cell">{t('provider.scorecard.colDecisionHours')}</th>
                        <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium hidden sm:table-cell">{t('provider.scorecard.colVsPlatform')}</th>
                        <th className="text-center py-2 text-[10px] uppercase text-slate-400 font-medium">{t('provider.scorecard.colTrend')}</th>
                        <th className="text-right py-2 text-[10px] uppercase text-slate-400 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {scorecards.map((sc, i) => {
                        const m = sc.metrics_snapshot || {};
                        const prevM = scorecards[i + 1]?.metrics_snapshot || {};
                        return (
                          <tr key={sc.week_start_utc} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`history-row-${sc.week_start_utc}`}>
                            <td className="py-2.5 font-medium text-[#122033]">{sc.week_start_utc}</td>
                            <td className="py-2.5 text-right">{m.quotes_sent_count ?? 0}</td>
                            <td className="py-2.5 text-right font-medium">
                              {m.acceptance_rate != null ? `${m.acceptance_rate}%` : '—'}
                            </td>
                            <td className="py-2.5 text-right hidden sm:table-cell">
                              {m.median_time_to_decision_hours != null ? `${m.median_time_to_decision_hours}` : '—'}
                            </td>
                            <td className="py-2.5 text-right hidden sm:table-cell">
                              {m.delta_acceptance_rate != null ? (
                                <span className={m.delta_acceptance_rate >= 0 ? 'text-emerald-600' : 'text-red-500'}>
                                  {m.delta_acceptance_rate > 0 ? '+' : ''}{m.delta_acceptance_rate}pp
                                </span>
                              ) : '—'}
                            </td>
                            <td className="py-2.5 text-center">
                              <TrendIcon current={m.acceptance_rate} previous={prevM.acceptance_rate} />
                            </td>
                            <td className="py-2.5 text-right">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedWeek(sc)}
                                className="text-[#3BA4C5] hover:text-[#2d8aa8] text-xs" data-testid={`view-detail-${sc.week_start_utc}`}>
                                {t('provider.scorecard.details')} <ChevronRight className="h-3 w-3 ml-0.5" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Detail Drawer */}
      <DetailDrawer scorecard={selectedWeek} onClose={() => setSelectedWeek(null)} />
    </div>
  );
}
